import { SummarySection } from '@/components/SummarySection';
import {
    selectBirthdate,
    selectLicenseExpiry,
    selectLicenseNumber,
    selectLicenseState,
    selectLicenseVersionNumber,
    selectResidencyStatus,
} from '@/context/borrower';
import {
    EJourneys,
    EModals,
    selectInitialJourney,
    selectIsNZ,
    setModal,
} from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { getDateFromState } from '@/utils/getDateFromState';
import { getStringFromState } from '@/utils/getStringFromState';

export const testId = 'PersonalInformation';

export interface IProps {
    showErrors: boolean;
    baseId: string;
}

export function PersonalInformation({
    showErrors,
    baseId,
}: IProps): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    // const gender = useAppSelector(selectGender);
    const birthdate = useAppSelector(selectBirthdate);
    const licenseNumber = useAppSelector(selectLicenseNumber);
    const licenseState = useAppSelector(selectLicenseState);
    const licenseExpiry = useAppSelector(selectLicenseExpiry);
    const licenseVersionNumber = useAppSelector(selectLicenseVersionNumber);
    const residencyStatus = useAppSelector(selectResidencyStatus);
    const initialJourney = useAppSelector(selectInitialJourney);
    const isNZ = useAppSelector(selectIsNZ);
    const isTestDrive = initialJourney === EJourneys.TEST_DRIVE;

    // ***** Event Handlers *****
    function handleClick(): void {
        dispatch(setModal(EModals.PERSONAL_INFORMATION));
    }

    // ***** Helpers *****
    function isComplete(): boolean {
        // if (gender === null || gender === undefined) return false;
        if (birthdate === null || birthdate === undefined) return false;
        // For NZ, check licenseVersionNumber instead of licenseNumber
        if (isNZ) {
            if (
                licenseVersionNumber === '' ||
                licenseVersionNumber === undefined
            )
                return false;
        } else {
            if (licenseNumber === '' || licenseNumber === undefined)
                return false;
        }
        if (licenseState === '' || licenseState === undefined) return false;
        if (licenseExpiry === null || licenseExpiry === undefined) return false;
        if (residencyStatus === null || residencyStatus === undefined)
            return false;
        if (
            licenseExpiry === null ||
            licenseExpiry === undefined ||
            licenseExpiry.toString() === 'Invalid Date'
        )
            return false;
        if (
            birthdate === null ||
            birthdate === undefined ||
            birthdate.toString() === 'Invalid Date'
        )
            return false;
        return true;
    }

    function fullText(): string {
        // For NZ, show "Licence Version Number" instead of "license number"
        const licenseField = isNZ ? 'Licence Version Number' : 'license number';
        const licenseValue = isNZ ? licenseVersionNumber : licenseNumber;

        const personalInfo = `My birthdate is <strong>${getDateFromState(
            birthdate
        )}</strong> and my ${licenseField} is <strong>${getStringFromState(
            licenseValue
        )}</strong>. My license is valid in <strong>${getStringFromState(
            licenseState
        )}</strong> and expires on the <strong>${getDateFromState(
            licenseExpiry
        )}</strong>`;

        if (isTestDrive) {
            return personalInfo;
        }

        return `${personalInfo}. I am a <strong>${getStringFromState(
            residencyStatus
        ).replace('_', ' ')}</strong>`;
    }

    // ***** Render *****
    return (
        <div data-testid={testId}>
            <SummarySection
                onClick={handleClick}
                id={`${baseId}-button-editPersonalInformation`}
                text={fullText()}
                heading='Personal Information'
                showErrors={showErrors && !isComplete()}
            />
        </div>
    );
}
